import { type CSSProperties } from 'react';

interface Props {
  pinned: boolean;
  setPinned: (v: boolean) => void;
}

const navBtnStyle = (disabled?: boolean): CSSProperties => ({
  width: 26,
  height: 26,
  background: 'transparent',
  border: 'none',
  borderRadius: '50%',
  cursor: 'default',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: disabled ? 'oklch(0.82 0.01 260)' : 'oklch(0.45 0.02 260)',
});

export default function ChromeToolbarVisual({ pinned }: Props) {
  return (
    <div className="relative" style={{ maxWidth: 820 }}>
      {/* Browser window */}
      <div
        className="bg-surface overflow-hidden"
        style={{
          borderRadius: 12,
          border: '1px solid var(--color-hairline)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        {/* Title bar */}
        <div
          className="flex items-center"
          style={{
            gap: 10,
            padding: '10px 14px',
            background: 'oklch(0.94 0.01 85)',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <div className="flex" style={{ gap: 6 }}>
            <div className="w-[11px] h-[11px] rounded-full" style={{ background: 'oklch(0.72 0.17 25)' }} />
            <div className="w-[11px] h-[11px] rounded-full" style={{ background: 'oklch(0.85 0.16 85)' }} />
            <div className="w-[11px] h-[11px] rounded-full" style={{ background: 'oklch(0.76 0.16 145)' }} />
          </div>
          <div className="flex-1" />
        </div>

        {/* Tab bar */}
        <div
          className="flex items-end"
          style={{ gap: 2, padding: '6px 10px 0', background: 'oklch(0.94 0.01 85)' }}
        >
          <div
            className="flex items-center bg-surface text-ink font-medium"
            style={{
              gap: 8,
              padding: '7px 14px 8px',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              borderTop: '1px solid var(--color-hairline)',
              borderLeft: '1px solid var(--color-hairline)',
              borderRight: '1px solid var(--color-hairline)',
              minWidth: 160,
              maxWidth: 220,
              fontSize: 12,
            }}
          >
            <div
              className="rounded font-mono font-extrabold flex items-center justify-center"
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                fontSize: 7,
              }}
            >
              go/
            </div>
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">New Tab</span>
          </div>
          <button
            className="cursor-pointer text-ink-muted"
            style={{
              background: 'transparent',
              border: 'none',
              padding: '6px 8px',
              marginBottom: 2,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {/* Nav + address bar row */}
        <div
          className="flex items-center bg-surface"
          style={{
            gap: 8,
            padding: '10px 12px',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <div className="flex text-ink-muted" style={{ gap: 2 }}>
            <button style={navBtnStyle()}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button style={navBtnStyle(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <button style={navBtnStyle()}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </button>
          </div>

          <div
            className="flex-1 flex items-center font-mono text-ink-muted"
            style={{
              background: 'var(--color-bg-alt)',
              border: '1px solid var(--color-hairline)',
              borderRadius: 999,
              padding: '7px 14px',
              fontSize: 12,
              gap: 8,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          {/* pinned go/ icon */}
          {pinned && (
            <button
              title="Tidy Go Links"
              className="font-mono font-extrabold flex items-center justify-center cursor-pointer"
              style={{
                width: 30,
                height: 30,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                border: '1.5px solid var(--accent-solid)',
                borderRadius: 7,
                fontSize: 11,
                animation: 'c2pulseIn 300ms ease-out',
              }}
            >
              go/
            </button>
          )}

          {/* Puzzle piece + tooltip */}
          <div
            className="flex items-center justify-center text-ink-soft relative"
            style={{
              width: 30,
              height: 30,
              background: 'transparent',
              borderRadius: 7,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'translateX(5px)' }}>
              <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7s2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z" />
            </svg>
            {!pinned && (
              <div
                className="absolute font-semibold whitespace-nowrap"
                style={{
                  top: -42,
                  right: -10,
                  background: 'var(--color-ink)',
                  color: '#fff',
                  padding: '7px 13px',
                  borderRadius: 999,
                  fontSize: 13,
                  animation: 'c2bounce 1.8s ease-in-out infinite',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                1. Click Chrome's puzzle piece
                <span
                  className="absolute"
                  style={{
                    bottom: -5,
                    right: 14,
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: '5px solid var(--color-ink)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Profile avatar */}
          <div
            className="rounded-full flex items-center justify-center font-bold"
            style={{
              width: 30,
              height: 30,
              marginLeft: 15,
              background: 'var(--color-lemon)',
              color: 'var(--color-lemon-deep)',
              fontSize: 12,
            }}
          >
            R
          </div>
        </div>

        {/* Page body (empty) */}
        <div
          className="bg-bg"
          style={{ minHeight: 200 }}
        />
      </div>

      {/* Extensions menu popover */}
      {!pinned && (
        <div
          className="absolute bg-surface z-10"
          style={{
            top: 142,
            right: 52,
            width: 320,
            borderRadius: 10,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.3)',
            border: '1px solid var(--color-hairline)',
            padding: '10px 0',
            animation: 'c2fadeSlide 160ms ease-out',
          }}
        >
          <div
            className="font-semibold text-ink"
            style={{ padding: '6px 14px 12px', borderBottom: '1px solid var(--color-hairline)', fontSize: 13 }}
          >
            Extensions
          </div>

          <div
            className="flex items-center relative"
            style={{
              gap: 10,
              padding: '10px 14px',
              background: 'var(--accent-tint)',
            }}
          >
            <div
              className="flex items-center justify-center font-mono font-extrabold"
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                fontSize: 11,
              }}
            >
              go/
            </div>
            <span className="flex-1 text-ink font-medium" style={{ fontSize: 13 }}>
              Tidy Go Links
            </span>
            <div
              className="relative flex items-center justify-center text-ink-muted"
              style={{
                background: 'transparent',
                padding: 4,
                borderRadius: 4,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" x2="12" y1="17" y2="22" />
                <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
              </svg>
              <div
                className="absolute font-semibold whitespace-nowrap"
                style={{
                  top: -42,
                  right: -10,
                  background: 'var(--color-ink)',
                  color: '#fff',
                  padding: '7px 13px',
                  borderRadius: 999,
                  fontSize: 13,
                  animation: 'c2bounce 1.8s ease-in-out infinite',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                2. Click pin
                <span
                  className="absolute"
                  style={{
                    bottom: -5,
                    right: 14,
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: '5px solid var(--color-ink)',
                  }}
                />
              </div>
            </div>
          </div>

          {['1Password', 'React DevTools', 'Dark Reader'].map(ext => (
            <div
              key={ext}
              className="flex items-center"
              style={{ gap: 10, padding: '9px 14px', opacity: 0.5 }}
            >
              <div
                className="rounded"
                style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--color-bg-alt)' }}
              />
              <span className="flex-1 text-ink-soft" style={{ fontSize: 13 }}>{ext}</span>
              <div style={{ width: 14, height: 14, opacity: 0.4 }} />
            </div>
          ))}
        </div>
      )}
      <style>{`
        @keyframes c2pulseIn {
          0% { opacity: 0; transform: scale(0.6); }
          60% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes c2bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes c2fadeSlide {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
